import {
    Box,
    IconButton,
    Paper,
    Switch,
    TextField,
    Typography,
    Menu,
    MenuItem,
    Divider,
    Button,
    Stack,
    Alert,
} from "@mui/material";
import { memo, useEffect, useState } from "react";
import { Visibility, MoreVert, Delete, Add, Cancel, Info } from "@mui/icons-material";
import ResourcesView from "../../../components/resources";
import StarIcon from '../../assets/icons/star.png';
import RichTextEditor from "../../../components/rich-text/rich-text-editor";
import { TheoryQuestion } from "../../../../../models/assessment";
  
interface TheoryViewProps {
    item: TheoryQuestion;
    setSaved: React.Dispatch<React.SetStateAction<boolean>>;
    register: (id: string, getItem: () => TheoryQuestion) => void;
    type: number;
    auto_marker: boolean;
    saveFunction: () => void;
}
  
function TheoryView({
    item,
    setSaved,
    register,
    type,
    auto_marker,
    saveFunction
}: TheoryViewProps) {

    const [localItem, setLocalItem] = useState<TheoryQuestion>(item);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [resourceAnchorEl, setResourceAnchorEl] = useState<null | HTMLElement>(null);
    const [resourcesVisible, setResourcesVisible] = useState<boolean>(false);
    const [descriptionVisible, setDescriptionVisible] = useState<boolean>(false);
    const [editing, setEditing] = useState<boolean>(false);

    useEffect(() => {
        register(item.id, () => localItem);
    }, [item.id, localItem, register]);

    const handleChange = (field: keyof TheoryQuestion, value: any) => {
        setSaved(false);
        setLocalItem((prev) => ({ ...prev, [field]: value }));
        saveFunction();
    };

    const toggleRequired = () => {
        handleChange("required", !localItem.required);
    };

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const toggleResourcesVisibility = () => {
        setResourcesVisible(!resourcesVisible);
        handleMenuClose();
    };

    const toggleDescriptionVisibility = () => {
        setDescriptionVisible(!descriptionVisible);
        handleMenuClose();
    };

    const handleImageClick = () => {
        setEditing(true);
    };

    const handleDoneClick = () => {
        setEditing(false);
    };

    const handleAddCorrectAnswer = () => {
        handleChange("rules", [...localItem.rules, ""]); 
    };

    const handleCorrectAnswerChange = (index: number, value: string) => {
        const updatedAnswers: any[] = [...localItem.rules];
        updatedAnswers[index] = value;
        handleChange("rules", updatedAnswers); 
    };

    const handleRemoveCorrectAnswer = (index: number) => {
        const updatedAnswers = localItem.rules.filter((_, i) => i !== index);
        handleChange("rules", updatedAnswers); 
    };

    return (
        <Paper
            sx={{
                backgroundColor: "#fff",
                padding: 2,
                display: "flex",
                flexDirection: "column",
                transition: "all 0.3s ease",
                gap: 2
            }}
        >
        {!editing ? (
            <>

                <RichTextEditor
                    id={localItem.id}
                    type="title"
                    value={localItem.title}
                    onChange={(val) => handleChange("title", val)}
                    fullBorder={false}
                    userPlaceholder={item.type === 1 ? "Untitled Short Text Question" : "Untitled Paragraph Question"}
                />

                {descriptionVisible && (
                    <RichTextEditor
                        id={localItem.id}
                        type="description"
                        value={localItem.description}
                        onChange={(val) => handleChange("description", val)}
                    />
                )}

                {resourcesVisible && (
                    <>
                        <Box
                            onClick={(event: React.MouseEvent<HTMLElement>) => setResourceAnchorEl(event.currentTarget)}
                            sx={{
                                width: 50,
                                height: 50,
                                minWidth: 50,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                border: "1px dashed #ccc",
                                flexShrink: 0,
                                borderRadius: 2,
                            }}
                        >
                            <Add sx={{ fontSize: 24, color: "#757575" }} />
                        </Box>

                        <Stack direction="row" spacing={1} alignItems="center" sx={{ overflowX: "auto", width: "100%", pb: 1 }}>
                            {localItem.resources.map((res, i) => {
                                return (
                                    <Paper
                                        key={`${res}-resource-${i}`}
                                        sx={{
                                        width: 50,
                                        height: 50,
                                        minWidth: 50,
                                        overflow: "hidden",
                                        position: "relative",
                                        borderRadius: 1,
                                        backgroundColor: "#000",
                                        flexShrink: 0,
                                        }}
                                    >
                                        <img
                                            src={res}
                                            alt={`resource-${i}`}
                                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                        />

                                        <IconButton
                                            size="small"
                                            sx={{
                                                position: "absolute",
                                                top: 1,
                                                right: 0.5,
                                                backgroundColor: "rgba(0,0,0,0.3)",
                                                color: "#fff",
                                                padding: "2px",
                                                "&:hover": {
                                                backgroundColor: "rgba(0,0,0,0.5)",
                                                },
                                            }}
                                            onClick={() => {
                                                handleChange("resources", localItem.resources.filter((r) => r !== res));
                                            }}
                                        >
                                            <Cancel fontSize="small" />
                                        </IconButton>
                                    </Paper>
                                );
                            })}
                        </Stack>

                        <ResourcesView
                            id={localItem.id}
                            resourceAnchorEl={resourceAnchorEl}
                            setResourceAnchorEl={setResourceAnchorEl}
                            onselect={(res: string) => handleChange("resources", [...localItem.resources, res])}
                        />
                    </>
                )}


                <TextField
                    variant="standard"
                    placeholder={type === 1 ? "Short answer text" : "Long answer text"}
                    disabled
                    fullWidth
                />

                {type === 2 && <Divider sx={{ my: 2 }} />}

            
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    <Box sx={{ width: 24, height: 24 }}>
                        {auto_marker ? (
                            <img
                                src={StarIcon}
                                alt="AutoMarker"
                                style={{ width: 24, height: 24, cursor: 'pointer' }}
                                onClick={handleImageClick}
                            />
                        ) : null}
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="body2">Required</Typography>
                        <Switch checked={!!localItem.required} onChange={toggleRequired} />

                        <IconButton onClick={handleMenuOpen} sx={{ marginLeft: 1 }}>
                            <MoreVert />
                        </IconButton>

                        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                            <MenuItem onClick={toggleResourcesVisibility}>
                                {resourcesVisible ? "Hide resources" : "Show resources"}
                            </MenuItem>
                            <MenuItem onClick={toggleDescriptionVisibility}>
                                {descriptionVisible ? "Hide description" : "Show description"}
                            </MenuItem>
                        </Menu>
                    </Box>
                </Box>

            </>
        ) : (
            <>
            
                <TextField
                    variant="standard"
                    value={localItem.marks}
                    onChange={(e) => handleChange("marks", parseInt(e.target.value))}
                    label="Marks"
                    type="number"
                    sx={{ maxWidth: "50%" }}
                />

                <Box sx={{ mb: 2 }}>
                    <Stack spacing={1} sx={{ mt: 2 }}>
                        <Typography variant="body2" fontWeight="bold">
                            Rules
                        </Typography>
                        <Alert severity="info">
                            Always keep your rules straightforward and concise.
                        </Alert>

                        {localItem.rules.map((answer, i) => (
                            <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                               <TextField
                                    variant="outlined"
                                    value={answer}
                                    onChange={(e) => handleCorrectAnswerChange(i, e.target.value)}
                                    fullWidth
                                    multiline
                                    placeholder="write a rule here"
                                    minRows={1.2}
                                    maxRows={1.2}
                                    sx={{ width: '100%' }}
                                />

                                <IconButton onClick={() => handleRemoveCorrectAnswer(i)}>
                                    <Delete />
                                </IconButton>
                            </Box>
                        ))}

                        <Box
                            onClick={handleAddCorrectAnswer}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                opacity: 0.6,
                                cursor: "pointer",
                                "&:hover": { opacity: 0.8 },
                            }}
                        >
                            <Typography variant="body2">Add another Rule</Typography>
                        </Box>
                    </Stack>
                </Box>

                <Button
                    variant="contained"
                    onClick={handleDoneClick}
                    sx={{
                        alignSelf: "flex-end",
                        marginTop: 2,
                        paddingLeft: 6,
                        paddingRight: 6,
                        backgroundColor: "#53826b",
                    }}
                >
                    Done
                </Button>
            </>
        )}
        </Paper>
    );
}

export default memo(TheoryView);
